import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Sprout, ArrowRight, MapPin, Check, Flower2, Trees, X, MapPinned } from 'lucide-react';

import { useTour } from '@/welcome/TourContext';
import { RegionPicker } from '@/components/RegionPicker';
import { HelpCentre, HelpSpotlight } from '@/components/help/HelpCentre';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = {
  forest: '#1A4331', deep: '#0F2D1F', lime: '#B4D330', leaf: '#6B9F4A', cream: '#FAF6EC',
  sage: '#E5EBDD', gold: '#C9A85B', terra: '#E2725B', muted: '#5C665C', sky: '#8FB4D6',
};
const DIM = 'rgba(15,45,31,0.6)';

export default function TourOverlay() {
  const { active, stage, helpOpen, helpSpot } = useTour();
  return (
    <>
      {active && stage && <StageRenderer stage={stage} />}
      {helpOpen && <HelpCentre />}
      {helpSpot && <HelpSpotlight guide={helpSpot} />}
    </>
  );
}

function StageRenderer({ stage }) {
  if (stage.kind === 'full-welcome') return <WelcomeMoment />;
  if (stage.kind === 'card-profile') return <GardenProfile />;
  if (stage.kind === 'full-ready') return <ReadyScreen />;
  if (stage.kind === 'floating') return <FloatingCard />;
  if (stage.kind === 'exchange') return <ExchangeStage />;
  return <Spotlight />;
}

// ── shared bits ───────────────────────────────────────────────────────────────
function Atmosphere() {
  return (
    <div className="fixed inset-0" style={{ zIndex: 1000, overflow: 'hidden', background: 'linear-gradient(180deg,#EDF1E5 0%,#E5EBDD 50%,#FAF6EC 100%)' }}>
      <div style={{ position: 'absolute', top: '12%', left: '-16%', width: 360, height: 360, borderRadius: '50%', background: C.lime, opacity: 0.35, filter: 'blur(20px)', animation: 'hgDrift 7s ease-in-out infinite' }} />
      <div style={{ position: 'absolute', bottom: '8%', right: '-18%', width: 400, height: 400, borderRadius: '50%', background: C.sky, opacity: 0.3, filter: 'blur(20px)', animation: 'hgDrift 9s ease-in-out infinite reverse' }} />
      <style>{`@keyframes hgDrift{0%{transform:translateY(-24px) scale(1)}100%{transform:translateY(28px) scale(1.12)}}
        @keyframes hgPingSlow{0%{transform:scale(1);opacity:.6}100%{transform:scale(1.9);opacity:0}}`}</style>
    </div>
  );
}

function ProgressDots({ index, onDark }) {
  return (
    <div className="flex items-center justify-center gap-1.5" data-testid="web-welcome-progress">
      {Array.from({ length: 7 }).map((_, i) => {
        const active = i === index;
        const done = i < index;
        const bg = active ? (onDark ? C.lime : C.forest) : done ? C.leaf : (onDark ? 'rgba(255,255,255,0.4)' : '#B7C3B0');
        return <span key={i} style={{ height: 7, width: active ? 22 : 7, borderRadius: 4, background: bg, transition: 'all .28s' }} />;
      })}
    </div>
  );
}

function PillButton({ children, onClick, testID, ghost }) {
  return (
    <button data-testid={testID} onClick={onClick}
      className="hg-press w-full flex items-center justify-center gap-2 rounded-full font-bold transition-all"
      style={ghost
        ? { height: 52, color: C.forest, background: 'transparent' }
        : { height: 58, color: C.cream, background: C.forest, boxShadow: `0 0 22px ${C.leaf}99` }}>
      {children}
    </button>
  );
}

// ── Screen 1 ────────────────────────────────────────────────────────────────
function WelcomeMoment() {
  const { next } = useTour();
  return (
    <div className="fixed inset-0 flex flex-col" style={{ zIndex: 1001 }}>
      <Atmosphere />
      <div className="relative flex flex-col items-center pt-8" style={{ zIndex: 1002 }}><ProgressDots index={0} /></div>
      <div className="relative flex-1 flex flex-col items-center justify-center px-8 gap-10" style={{ zIndex: 1002 }}>
        <div className="relative flex items-center justify-center" style={{ width: 150, height: 150 }}>
          <span style={{ position: 'absolute', width: 150, height: 150, borderRadius: '50%', background: C.lime, animation: 'hgPingSlow 1.9s ease-out infinite' }} />
          <div className="flex items-center justify-center" style={{ width: 108, height: 108, borderRadius: '50%', background: C.forest, boxShadow: '0 20px 50px -18px rgba(15,45,31,.7)' }}>
            <Sprout style={{ width: 58, height: 58, color: C.lime }} />
          </div>
        </div>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, type: 'spring', damping: 16 }} className="text-center">
          <h1 className="font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest, fontSize: 40, lineHeight: 1.1 }}>Welcome to Homegrown.</h1>
          <p className="mt-3" style={{ color: C.muted, fontSize: 18 }}>Your personal garden companion.</p>
        </motion.div>
      </div>
      <div className="relative px-8 pb-10 w-full max-w-md mx-auto" style={{ zIndex: 1002 }}>
        <PillButton testID="welcome-start-btn" onClick={next}>Show me around <ArrowRight style={{ width: 20, height: 20, color: C.lime }} /></PillButton>
      </div>
    </div>
  );
}

// ── Screen 2 ────────────────────────────────────────────────────────────────
const LEVELS = [
  { id: 'beginner', label: 'New Gardener', Icon: Sprout },
  { id: 'intermediate', label: 'Enthusiastic Amateur', Icon: Flower2 },
  { id: 'advanced', label: 'Seasoned Grower', Icon: Trees },
];
function GardenProfile() {
  const { next, user, refreshUser } = useTour();
  const [region, setRegion] = useState(user?.region || '');
  const [level, setLevel] = useState(user?.skill_level || 'beginner');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try { await axios.patch(`${API}/users/me`, { region, skill_level: level }, { withCredentials: true }); await refreshUser(); } catch { /* ignore */ }
    next();
  };

  return (
    <div className="fixed inset-0 flex flex-col" style={{ zIndex: 1001 }}>
      <Atmosphere />
      <div className="relative flex flex-col items-center pt-8" style={{ zIndex: 1002 }}><ProgressDots index={1} /></div>
      <div className="relative flex-1 flex items-center justify-center px-6" style={{ zIndex: 1002 }}>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ type: 'spring', damping: 16 }}
          className="hg-glass rounded-3xl p-7 w-full max-w-lg">
          <h2 className="font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest, fontSize: 24 }}>Your garden profile</h2>
          <p className="mt-1 mb-5" style={{ color: C.muted, fontSize: 14 }}>We’ll personalise everything for you.</p>

          <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: C.muted }}>Where you grow</p>
          <RegionPicker value={region} onChange={(id) => setRegion(id)} testId="welcome-region-select" />

          <p className="text-[11px] font-semibold uppercase tracking-wider mb-2 mt-5" style={{ color: C.muted }}>Your experience</p>
          <div className="grid grid-cols-3 gap-2.5">
            {LEVELS.map((lv) => {
              const on = level === lv.id;
              return (
                <button key={lv.id} data-testid={`welcome-level-${lv.id}`} onClick={() => setLevel(lv.id)}
                  className="hg-press relative flex flex-col items-center gap-2 rounded-2xl py-4 px-2 transition-all"
                  style={{ background: on ? 'rgba(107,159,74,0.14)' : '#fff', border: `1.5px solid ${on ? C.leaf : 'rgba(26,67,49,0.1)'}` }}
                  aria-pressed={on}>
                  <span className="flex items-center justify-center rounded-full" style={{ width: 44, height: 44, background: on ? C.leaf : 'rgba(107,159,74,0.14)' }}>
                    <lv.Icon style={{ width: 22, height: 22, color: on ? C.cream : '#4b7a33' }} />
                  </span>
                  <span className="text-[12px] font-semibold text-center leading-tight" style={{ color: on ? C.forest : C.muted }}>{lv.label}</span>
                </button>
              );
            })}
          </div>
        </motion.div>
      </div>
      <div className="relative px-8 pb-10 w-full max-w-md mx-auto" style={{ zIndex: 1002 }}>
        <PillButton testID="welcome-profile-continue" onClick={region ? save : undefined}>
          <span style={{ opacity: region && !saving ? 1 : 0.5 }}>Looks good</span> <ArrowRight style={{ width: 20, height: 20, color: C.lime }} />
        </PillButton>
      </div>
    </div>
  );
}

// ── Screen 7 ────────────────────────────────────────────────────────────────
function ReadyScreen() {
  const { complete } = useTour();
  return (
    <div className="fixed inset-0 flex flex-col" style={{ zIndex: 1001 }}>
      <Atmosphere />
      <div className="relative flex flex-col items-center pt-8" style={{ zIndex: 1002 }}><ProgressDots index={6} /></div>
      <div className="relative flex-1 flex flex-col items-center justify-center px-8 gap-10" style={{ zIndex: 1002 }}>
        <div className="relative flex items-center justify-center" style={{ width: 130, height: 130 }}>
          <span style={{ position: 'absolute', width: 110, height: 110, borderRadius: '50%', border: `3px solid ${C.leaf}`, animation: 'hgPingSlow 1.6s ease-out infinite' }} />
          <div className="flex items-center justify-center" style={{ width: 104, height: 104, borderRadius: '50%', background: C.leaf, boxShadow: '0 20px 50px -18px rgba(15,45,31,.7)' }}>
            <Check style={{ width: 52, height: 52, color: C.cream }} />
          </div>
        </div>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12, type: 'spring', damping: 16 }} className="text-center">
          <h1 className="font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest, fontSize: 36, lineHeight: 1.1 }}>You are ready to grow.</h1>
          <p className="mt-3" style={{ color: C.muted, fontSize: 18 }}>Your personalised garden dashboard is set up.</p>
        </motion.div>
      </div>
      <div className="relative px-8 pb-10 w-full max-w-md mx-auto space-y-2" style={{ zIndex: 1002 }}>
        <PillButton testID="welcome-start-growing" onClick={complete}>Start growing</PillButton>
        <PillButton testID="welcome-explore" onClick={complete} ghost>Explore on my own</PillButton>
      </div>
    </div>
  );
}

// ── Spotlight coach-mark ──────────────────────────────────────────────────────
function useTargetRect(selector, stageId) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    setRect(null);
    let timer; let tries = 0;
    const measure = () => {
      const el = document.querySelector(selector);
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) { setRect({ x: r.left, y: r.top, width: r.width, height: r.height }); return true; }
      }
      return false;
    };
    const poll = () => { if (measure()) return; tries += 1; if (tries < 26) timer = setTimeout(poll, 140); };
    poll();
    const onMove = () => measure();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => { clearTimeout(timer); window.removeEventListener('resize', onMove); window.removeEventListener('scroll', onMove, true); };
  }, [selector, stageId]);
  return rect;
}

function Tooltip({ title, body, prompt, children }) {
  return (
    <div className="hg-glass rounded-2xl shadow-xl p-4" style={{ maxWidth: 340, pointerEvents: 'auto' }}>
      <p className="font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest, fontSize: 16 }}>{title}</p>
      {body && <p className="mt-1.5" style={{ color: C.muted, fontSize: 13, lineHeight: 1.5 }}>{body}</p>}
      {prompt && (
        <div className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5" style={{ background: 'rgba(107,159,74,0.14)' }}>
          <span style={{ width: 8, height: 8, borderRadius: 4, background: C.leaf, animation: 'hgPingSlow 1.4s ease-out infinite' }} />
          <span className="text-[12px] font-semibold" style={{ color: '#4b7a33' }}>{prompt}</span>
        </div>
      )}
      {children}
    </div>
  );
}

function Spotlight() {
  const { stage, next, complete } = useTour();
  const rect = useTargetRect(stage.target, stage.id);
  const PAD = 10;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;

  const Header = (
    <div className="fixed left-0 right-0 flex items-center justify-center" style={{ top: 22, zIndex: 1006, pointerEvents: 'none' }}>
      <ProgressDots index={stage.dot} onDark />
      <button data-testid="welcome-skip" onClick={complete} className="absolute text-[13px] font-semibold" style={{ right: 24, color: 'rgba(255,255,255,0.85)', pointerEvents: 'auto' }}>Skip</button>
    </div>
  );

  if (!rect) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-6" style={{ zIndex: 1005, background: DIM }}>
        {Header}
        <div className="flex flex-col items-center gap-3" style={{ maxWidth: 360 }}>
          <Tooltip title={stage.title} body={stage.body} />
          <button data-testid="welcome-spotlight-advance" onClick={next} className="hg-press rounded-full px-6 py-2.5 font-semibold" style={{ background: C.forest, color: C.cream }}>
            {stage.prompt || 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  const holeTop = rect.y - PAD, holeLeft = rect.x - PAD, holeW = rect.width + PAD * 2, holeH = rect.height + PAD * 2;
  const onLeft = rect.x + rect.width < vw * 0.42;
  const below = rect.y + rect.height / 2 < vh / 2;

  let tipStyle;
  if (onLeft) tipStyle = { top: Math.min(Math.max(rect.y + rect.height / 2 - 70, 70), vh - 200), left: holeLeft + holeW + 16 };
  else if (below) tipStyle = { top: holeTop + holeH + 16, left: Math.min(Math.max(rect.x + rect.width / 2 - 170, 16), vw - 356) };
  else tipStyle = { bottom: vh - holeTop + 16, left: Math.min(Math.max(rect.x + rect.width / 2 - 170, 16), vw - 356) };

  const advance = () => {
    if (stage.clickTarget) { try { document.querySelector(stage.target)?.click?.(); } catch { /* ignore */ } }
    next();
  };

  const blk = (s) => <div style={{ position: 'fixed', background: DIM, ...s }} />;

  return (
    <div className="fixed inset-0" style={{ zIndex: 1005 }}>
      {blk({ top: 0, left: 0, right: 0, height: Math.max(0, holeTop) })}
      {blk({ top: holeTop + holeH, left: 0, right: 0, bottom: 0 })}
      {blk({ top: holeTop, left: 0, width: Math.max(0, holeLeft), height: holeH })}
      {blk({ top: holeTop, left: holeLeft + holeW, right: 0, height: holeH })}

      <div style={{ position: 'fixed', top: holeTop, left: holeLeft, width: holeW, height: holeH, borderRadius: 16, border: `2.5px solid ${C.gold}`, boxShadow: `0 0 0 3px ${C.gold}40, 0 0 30px ${C.gold}80`, animation: 'hgPulseRing 1.4s ease-in-out infinite', pointerEvents: 'none' }} />
      <style>{`@keyframes hgPulseRing{0%,100%{opacity:.6}50%{opacity:1}}`}</style>

      {stage.prompt ? (
        <button data-testid="welcome-spotlight-advance" onClick={advance}
          style={{ position: 'fixed', top: holeTop, left: holeLeft, width: holeW, height: holeH, background: 'transparent', cursor: 'pointer', border: 'none' }} />
      ) : null}

      {Header}

      <div style={{ position: 'fixed', zIndex: 1006, pointerEvents: 'none', ...tipStyle }}>
        <Tooltip title={stage.title} body={stage.body} prompt={stage.prompt}>
          {stage.info ? (
            <button data-testid="welcome-next" onClick={next} className="hg-press mt-3 inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-semibold text-[12px]" style={{ background: C.forest, color: C.cream, pointerEvents: 'auto' }}>
              Next <ArrowRight style={{ width: 14, height: 14, color: C.lime }} />
            </button>
          ) : null}
        </Tooltip>
      </div>
    </div>
  );
}

// ── Screen 5 follow-up ────────────────────────────────────────────────────────
function FloatingCard() {
  const { stage, next } = useTour();
  return (
    <div className="fixed inset-0" style={{ zIndex: 1005, pointerEvents: 'none' }}>
      <div className="fixed left-0 right-0 flex justify-center" style={{ top: 22 }}><ProgressDots index={stage.dot} onDark /></div>
      <div className="fixed left-1/2 -translate-x-1/2 bottom-8 w-full max-w-md px-4" style={{ pointerEvents: 'auto' }}>
        <div className="hg-glass rounded-2xl shadow-xl p-4 flex items-center gap-3">
          <span className="flex items-center justify-center rounded-full flex-shrink-0" style={{ width: 44, height: 44, background: 'rgba(107,159,74,0.16)' }}>
            <MapPinned style={{ width: 22, height: 22, color: '#4b7a33' }} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-bold" style={{ color: C.forest, fontSize: 15 }}>{stage.title}</p>
            <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.4 }}>{stage.body}</p>
          </div>
          <button data-testid="welcome-floating-next" onClick={next} className="hg-press flex items-center justify-center rounded-full flex-shrink-0" style={{ width: 40, height: 40, background: C.forest }}>
            <ArrowRight style={{ width: 18, height: 18, color: C.cream }} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Screen 6 — self-contained Exchange intro ──────────────────────────────────
function ExchangeStage() {
  const { stage, next } = useTour();
  const advanced = useRef(false);
  useEffect(() => {
    const t = setTimeout(() => { if (!advanced.current) { advanced.current = true; next(); } }, stage.autoMs || 4200);
    return () => clearTimeout(t);
  }, [next, stage.autoMs]);

  return (
    <button data-testid="welcome-exchange" onClick={() => { if (!advanced.current) { advanced.current = true; next(); } }}
      className="fixed inset-0 flex flex-col items-center justify-center" style={{ zIndex: 1005, background: 'rgba(15,45,31,0.82)', border: 'none', cursor: 'pointer' }}>
      <div className="absolute left-0 right-0 flex justify-center" style={{ top: 22 }}><ProgressDots index={stage.dot} onDark /></div>
      <div className="relative flex items-center justify-center mb-7" style={{ width: 300, height: 184, borderRadius: 24, background: 'rgba(255,255,255,0.08)', border: `1px solid ${C.lime}59` }}>
        <MapPin style={{ position: 'absolute', top: 28, left: 54, width: 18, height: 18, color: `${C.gold}cc` }} />
        <MapPin style={{ position: 'absolute', bottom: 34, right: 60, width: 16, height: 16, color: `${C.sky}cc` }} />
        <MapPin style={{ position: 'absolute', bottom: 40, left: 78, width: 15, height: 15, color: `${C.leaf}cc` }} />
        <div className="relative flex items-center justify-center" style={{ width: 90, height: 90 }}>
          <span style={{ position: 'absolute', width: 64, height: 64, borderRadius: '50%', background: C.terra, animation: 'hgPingSlow 1.2s ease-out infinite' }} />
          <MapPin style={{ width: 46, height: 46, color: C.terra }} />
        </div>
      </div>
      <div className="px-6" style={{ maxWidth: 380 }}>
        <div className="hg-glass rounded-2xl shadow-xl p-4 text-center">
          <p className="font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest, fontSize: 16 }}>{stage.title}</p>
          <p className="mt-1.5" style={{ color: C.muted, fontSize: 13, lineHeight: 1.5 }}>{stage.body}</p>
        </div>
        <p className="text-center mt-3 text-[12px] font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>Tap to continue</p>
      </div>
    </button>
  );
}
